import { View } from "@amzn/stencil-react-components/layout";
import {
  ModalContainer,
  WithModal,
} from "@amzn/stencil-react-components/modal";
import { useContext, useState } from "react";

import {
  publishSecurityKeyIssuesModalClosedMetrics,
  publishSecurityKeyIssuesModalOpenedMetrics,
} from "@/apis/Metrics";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";

import ReportSecurityKeyIssuesForm, {
  FormConfig,
  ReportFormData,
} from "./ReportSecurityKeyIssuesForm";
import ReportSecurityKeyIssuesSubmission from "./ReportSecurityKeyIssuesSubmission";

const MODAL_MAX_WIDTH = "842px";

const DEFAULT_FORM_CONFIG: FormConfig = {
  warningBannerMessageId:
    "insertAndTestSecurityKey-issuesForm-warningBannerText-default",
  question1Options: [
    {
      id: "reason-not-arrived",
      value: "not_arrived",
      labelMessageId:
        "insertAndTestSecurityKey-issuesForm-question1-default-option1",
    },
    {
      id: "reason-not-with-me",
      value: "not_with_me",
      labelMessageId:
        "insertAndTestSecurityKey-issuesForm-question1-default-option2",
    },
  ],
};

const TIMEOUT_FORM_CONFIG: FormConfig = {
  warningBannerMessageId:
    "insertAndTestSecurityKey-issuesForm-warningBannerText-timeout",
  question1Options: [
    {
      id: "reason-key-damaged",
      value: "key_damaged",
      labelMessageId:
        "insertAndTestSecurityKey-issuesForm-question1-timeout-option1",
    },
    {
      id: "reason-not-detecting",
      value: "not_detecting",
      labelMessageId:
        "insertAndTestSecurityKey-issuesForm-question1-timeout-option2",
    },
    // TODO: Add this as an option for when we build Scenario 6 for FIPS Keys:
    // https://taskei.amazon.dev/tasks/NHE-4963
    // {
    //   id: "reason-fips-key",
    //   value: "fips_key",
    //   labelMessageId:
    //     "insertAndTestSecurityKey-issuesForm-question1-timeout-option3",
    // },
  ],
};

/**
 * Props for the ReportSecurityKeyIssuesModal component.
 */
interface ReportSecurityKeyIssuesModalProps {
  /** Render prop function that provides modal controls to trigger element */
  children: (params: { open: () => void; isOpen: boolean }) => JSX.Element;
  /** Whether to show timeout-specific form content and options */
  shouldShowTimeoutContent?: boolean;
}

/**
 * Represents the current screen being displayed within the modal.
 *
 * - `form`: Initial form screen for collecting issue details
 * - `submission`: Confirmation screen after successful form submission
 */
type ScreenType = "form" | "submission";

/**
 * Modal wrapper component for reporting security key issues and requesting assistance.
 *
 * The modal supports two main use cases:
 * 1. **General issues**: Standard form for users reporting missing or damaged keys
 * 2. **Timeout scenarios**: Specialized form for users experiencing detection/validation failures
 *
 * @param props - The component props
 * @param props.children - Render prop function that receives modal controls
 * @param props.shouldShowTimeoutContent - Whether to use timeout-specific form configuration
 * @returns JSX element containing the modal with issue reporting workflow
 */
export default function ReportSecurityKeyIssuesModal({
  children,
  shouldShowTimeoutContent = false,
}: ReportSecurityKeyIssuesModalProps): JSX.Element {
  const { dispatch } = useContext(AppContext);
  const [screenData, setScreenData] = useState<{
    screen: ScreenType;
    formData: ReportFormData | null;
  }>({
    screen: "form",
    formData: null,
  });

  const formConfig = shouldShowTimeoutContent
    ? TIMEOUT_FORM_CONFIG
    : DEFAULT_FORM_CONFIG;

  /**
   * Handles form submission by transitioning to the submission confirmation screen.
   *
   * This function processes the completed form data and advances the modal to show
   * the appropriate confirmation screen based on the user's selected delivery method.
   * Future implementation will include actual form submission to backend services.
   *
   * @param formData - The completed form data containing issue details and delivery preferences
   */
  const handleFormSubmit = (formData: ReportFormData) => {
    setScreenData({ screen: "submission", formData });

    // TODO: [Implement form submission logic](https://taskei.amazon.dev/tasks/NHE-5154)
  };

  /**
   * Handles form cancellation by resetting the form state and closing the modal.
   *
   * Publishes cancellation metrics, resets the modal to its initial state,
   * and closes the modal interface. This ensures proper cleanup when users
   * decide not to complete the issue reporting process.
   *
   * @param close - Modal close function provided by the WithModal component
   */
  const handleFormCancel = (close: () => void) => {
    setScreenData({ screen: "form", formData: null });
    publishSecurityKeyIssuesModalClosedMetrics();
    close();
  };

  /**
   * Handles closing the submission confirmation screen and the modal.
   *
   * Publishes completion metrics and closes the modal after the user has
   * reviewed their submission confirmation. This completes the issue reporting
   * workflow and returns the user to the main security key testing interface.
   *
   * @param close - Modal close function provided by the WithModal component
   */
  const handleSubmissionClose = (close: () => void) => {
    publishSecurityKeyIssuesModalClosedMetrics();
    close();
  };

  /**
   * Opens the chat support flyout by dispatching the appropriate action.
   *
   * This function integrates with the global chat support system, allowing users
   * to get immediate assistance from IT support staff. It's particularly useful
   * when users select the "chat with IT" delivery option or need additional help
   * beyond the standard issue reporting workflow.
   */
  const openChatSupport = () => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_FLYOUT_VISIBLE,
      chatSupportFlyoutVisible: true,
    });
  };

  const renderModal = ({ close }: { close: () => void }) => {
    return (
      <ModalContainer maxWidth={MODAL_MAX_WIDTH}>
        <View
          backgroundColor="color.surface.bg-muted"
          className="security-key-modal"
        >
          {screenData.screen === "form" && (
            <ReportSecurityKeyIssuesForm
              config={formConfig}
              onSubmit={handleFormSubmit}
              onCancel={() => handleFormCancel(close)}
            />
          )}
          {screenData.screen === "submission" && screenData.formData && (
            <ReportSecurityKeyIssuesSubmission
              formData={screenData.formData}
              onClose={() => handleSubmissionClose(close)}
              onOpenChat={openChatSupport}
            />
          )}
        </View>
      </ModalContainer>
    );
  };

  return (
    <WithModal renderModal={renderModal} shouldCloseOnClickOutside={false}>
      {({ open, isOpen }) =>
        children({
          open: () => {
            publishSecurityKeyIssuesModalOpenedMetrics();
            open();
          },
          isOpen,
        })
      }
    </WithModal>
  );
}
